import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import { formatDateTimeForUser } from 'utils/dateTime';

import { WorkflowStepCompletion } from './workflowStepCompletion';

export const WorkflowStepCollapsedSummary = ({ completion }: { completion: WorkflowStepCompletion }) => (
  <Stack spacing={0.35} data-testid="workflow-step-collapsed-summary">
    <Typography variant="body2">{completion.summary}</Typography>
    {completion.acceptedAt ? (
      <Typography variant="body2" color="text.secondary">
        {`Marked done on ${formatDateTimeForUser(completion.acceptedAt)} by ${completion.acceptedByName}`}
      </Typography>
    ) : null}
  </Stack>
);

export const WorkflowStepDoneControls = ({
  stepLabel,
  completion,
  error,
  isSaving,
  savingAction,
  onMarkDone,
  onReopen,
  part = 'all',
}: {
  stepLabel: string;
  completion?: WorkflowStepCompletion;
  error?: string;
  isSaving: boolean;
  savingAction?: 'approved' | 'revoked';
  onMarkDone: () => void;
  onReopen: () => void;
  part?: 'all' | 'notices' | 'actions';
}) => {
  if (!completion || completion.mode === 'none' || completion.mode === 'evidence') {
    return null;
  }
  if (!completion.canMarkDone && !completion.showReopen && !completion.isStale && !completion.blockedReason && !error) {
    return null;
  }

  const notices = (
    <>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {completion.isStale ? (
        <Alert severity="warning">
          {`${completion.acceptedByName} marked this step done on ${formatDateTimeForUser(completion.acceptedAt)} for ${
            completion.acceptedSummary
          }. It's since changed to ${completion.summary} — mark as done again, or reopen.`}
        </Alert>
      ) : null}
      {completion.blockedReason ? (
        <Typography variant="caption" color="text.secondary">
          {completion.blockedReason}
        </Typography>
      ) : null}
    </>
  );
  const actions = (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
      {completion.canMarkDone ? (
        <Button
          size="small"
          aria-label={`Mark ${stepLabel} as done`}
          aria-busy={isSaving && savingAction === 'approved'}
          variant="contained"
          color="secondary"
          onClick={onMarkDone}
          disabled={isSaving}
          startIcon={
            isSaving && savingAction === 'approved' ? (
              <CircularProgress color="inherit" size={16} aria-label="Marking as done" />
            ) : undefined
          }
          sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
        >
          Mark as done
        </Button>
      ) : null}
      {completion.showReopen ? (
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={onReopen}
          disabled={isSaving}
          sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
        >
          Reopen step
        </Button>
      ) : null}
    </Stack>
  );

  if (part === 'notices') {
    if (!error && !completion.isStale && !completion.blockedReason) {
      return null;
    }
    return (
      <Stack spacing={1} sx={{ pt: 0.5 }}>
        {notices}
      </Stack>
    );
  }
  if (part === 'actions') {
    return actions;
  }

  return (
    <Stack spacing={1} sx={{ pt: 0.5 }} data-testid="workflow-step-done-controls">
      {notices}
      {actions}
    </Stack>
  );
};
